import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useAPI, useAnonymousAPI } from "./api"
import { DeliveryOptionInterface } from "../models/delivery-options"
import moment from "moment"
import { Product } from "../models/product"
import { CollectionMethod } from "../models/collection-method"
import { Account } from "../models/user"
import { Price } from "../models/price"
import { AddressType } from "../models/address"
import { getCachedOrder } from "./cached-order"
import {
  ProviderName,
  SupportedPaymentMethods,
} from "../models/payment-methods"
import { useI18next } from "gatsby-plugin-react-i18next"

export const cacheKeys = {
  restaurants: "restaurants",
  restaurant: "restaurant",
  user: "user",
  paymentIntent: "paymentIntent",
  addresses: "addresses",
  timeslots: "timeslots",
  recommendedProducts: "recommendedProducts",
  signup_info: "signup_info",
  token: "token",
  order: "order",
  orderByDisplayId: "orderByDisplayId",
  userActiveOrders: "userActiveOrders",
  adyenPaymentMethods: "adyenPaymentMethods",
  paymentMethods: "paymentMethods",
  adyenPaymentDetails: "adyenPaymentDetails",
  creditCards: "credit-cards",
  productLabels: "productLabels",
}

export const useProductLabels = ({ shopId }: { shopId?: string }) => {
  const api = useAPI()

  return useQuery({
    queryKey: [cacheKeys.productLabels],
    queryFn: async () => await api.restaurants.listProductLabels({ shopId }),
    refetchOnWindowFocus: true,
    enabled: !!shopId,
  })
}

export const useFetchRestaurant = (
  slug: string | undefined,
  params?: {
    requested_time?: string
    locale?: string
  }
) => {
  const api = useAPI()
  const user =
    typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("hg::cache::user")!)
      : null
  let queryKey = [cacheKeys.restaurant, slug, params]
  if (user?.access_token) {
    queryKey.push(user?.access_token)
  }
  return useQuery({
    queryKey,
    queryFn: async () => await api.restaurants.retrieve(slug!, params),
    enabled: !!slug,
  })
}

export const useFetchRestaurants = (payload?: {
  searchParams?: {
    showHidden?: boolean
    longitude?: number
    latitude?: number
  }
}) => {
  const anonymousAPI = useAnonymousAPI()

  return useQuery({
    queryKey: [cacheKeys.restaurants, payload],
    queryFn: async () =>
      await anonymousAPI.restaurants.list(payload?.searchParams),
  })
}

export const useRemoveProduct = (orderId?: string) => {
  const api = useAPI()
  const queryClient = useQueryClient()

  return useMutation<any, any, string>({
    mutationFn: api.checkout.removeFromOrder,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [cacheKeys.order, orderId] })
    },
  })
}

export const useFetchAddresses = (enabled: boolean) => {
  const api = useAPI()

  return useQuery({
    queryKey: [cacheKeys.addresses],
    queryFn: async () => await api.addresses.list(),
    refetchOnWindowFocus: false,
    enabled,
  })
}

export const useDeleteAddress = () => {
  const api = useAPI()

  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: api.addresses.delete,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [cacheKeys.addresses] })
    },
  })
}

export const useCreateAddress = () => {
  const api = useAPI()

  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: api.addresses.create,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [cacheKeys.addresses] })
    },
  })
}

export const useCheckAddressAvailability = () => {
  const api = useAPI()

  return useMutation({
    mutationFn: api.addresses.check,
  })
}

export const useGetTimeslotOptions = (
  url: string,
  date: string,
  deliveryAddress: AddressType | null
) => {
  const api = useAPI()

  return useQuery<DeliveryOptionInterface[], any>({
    queryKey: [cacheKeys.timeslots, date, deliveryAddress],
    queryFn: async () =>
      await api.restaurants.retrieveTimeslotOptions(
        url,
        moment(date),
        deliveryAddress
      ),
    refetchOnWindowFocus: false,
    enabled: !!date && moment(date).isValid(),
  })
}

export const useAccountType = () => {
  // TODO
  // once /me can be used for both guest and user, make use of that call in this hook
  // in order to determine account type
  // just refetch this query in `onSuccess` of `useGuestSignin`, `useSignin` and `useSignout`
  // in order to have fresh info all the time
  // call this hook in AccountContext.tsx
}

export const useActiveOrders = (
  user: Account | undefined,
  enabled: boolean
) => {
  const api = useAPI()

  return useQuery({
    queryKey: [cacheKeys.userActiveOrders, user?.id],
    queryFn: async () => {
      const orders = await api.orders.listActiveOrders()
      return orders
    },
    refetchOnWindowFocus: false,
    enabled,
  })
}

export const usePaymentMethods = ({
  orderId,
  supportedMethods,
}: {
  orderId: string
  supportedMethods: SupportedPaymentMethods
}) => {
  const api = useAPI()

  return useQuery({
    queryKey: [cacheKeys.paymentMethods, orderId],
    queryFn: async () => {
      return api.checkout.listPaymentMethods({
        orderId,
        supportedMethods,
      })
    },
    refetchOnWindowFocus: false,
    enabled: !!orderId,
  })
}

export const useAdyenPaymentMethods = ({
  amount,
  url,
}: {
  amount: Price
  url: string
}) => {
  const api = useAPI()

  return useQuery({
    queryKey: [cacheKeys.adyenPaymentMethods, url, amount],
    queryFn: async () => {
      const { payment_methods } = await api.checkout.listAdyenPaymentMethods({
        amount,
        url,
      })
      return payment_methods
    },
    refetchOnWindowFocus: false,
  })
}

export const useAdyenPaymentDetails = ({
  redirectResult,
}: {
  redirectResult: string
}) => {
  const api = useAPI()
  // The payment details url is cached in the order object, so we need to use that to fetch the payment details
  const cachedOrder = getCachedOrder()

  return useQuery({
    queryKey: [cacheKeys.adyenPaymentDetails, redirectResult],
    queryFn: async () => {
      return api.payments.getAdyenPaymentDetails({
        redirectResult,
        paymentDetailsUrl: cachedOrder?.adyenPaymentDetailsUrl!,
      })
    },
    refetchOnWindowFocus: false,
    // If for some reason the payment details url is not cached, we avoid fetching and rely on SSE
    enabled: !!cachedOrder?.adyenPaymentDetailsUrl,
  })
}

export const useMakeAdyenPayment = () => {
  const api = useAPI()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: api.payments.makeAdyenPayment,
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: [cacheKeys.creditCards],
      })
    },
  })
}

export const useWithdrawTermConsent = () => {
  const api = useAPI()

  return useMutation({
    mutationFn: api.profile.withdrawTermConsent,
  })
}

export const useAcceptTerm = () => {
  const api = useAPI()

  return useMutation({
    mutationFn: api.profile.acceptTerm,
  })
}

export const useUser = (
  {
    enabled,
    retry,
    accountType,
  }: { enabled?: boolean; retry?: boolean; accountType?: "user" | "guest" } = {
    enabled: true,
    retry: true,
  }
) => {
  const api = useAPI()

  return useQuery({
    queryKey: [cacheKeys.user, ...(accountType ? [accountType] : [])],
    queryFn: async () => {
      const { data: account } = await api.auth.me()
      return account
    },
    retry,
    refetchOnWindowFocus: false,
    enabled,
  })
}

export const useUpdateProfile = () => {
  const api = useAPI()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: api.auth.updateProfile,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [cacheKeys.user] })
    },
  })
}

export const useResetPassword = () => {
  const api = useAnonymousAPI()

  return useMutation({
    mutationFn: api.auth.resetPassword,
  })
}

export const useRequestEmailVerificationCode = () => {
  const api = useAnonymousAPI()

  return useMutation({
    mutationFn: api.auth.requestEmailVerification,
  })
}

export const useConfirmEmailVerification = () => {
  const api = useAnonymousAPI()

  return useMutation({
    mutationFn: api.auth.confirmEmailVerification,
  })
}

export const useGuestSignin = () => {
  const api = useAnonymousAPI()

  return useMutation({
    mutationFn: api.guest.guestSignin,
  })
}

export const useSignin = () => {
  const api = useAnonymousAPI()

  return useMutation({
    mutationFn: api.auth.signin,
  })
}

export const useSignup = () => {
  const api = useAnonymousAPI()

  return useMutation({
    mutationFn: api.auth.signup,
  })
}

export const useSignupLight = () => {
  const api = useAnonymousAPI()

  return useMutation({
    mutationFn: api.auth.signupLight,
  })
}

export const useSignout = () => {
  const api = useAPI()

  return useMutation<any, any>({
    mutationFn: api.auth.signout,
  })
}

export const useRecommendedProducts = (
  shopId: string | undefined,
  collectionMethod: CollectionMethod,
  data: Product[],
  isOpen: boolean,
  locale?: string
) => {
  const api = useAPI()
  const getPayload = () => ({
    shopId: shopId || "",
    collection_method: collectionMethod,
    locale,
    products: data.map((p) => {
      const additionOptions = (p.addition_options || [])
        // Filtering out addition options that have no additions
        .filter((ao) => {
          return ao.additions.some((a) => a.count > 0)
        })
        .map((ao) => {
          return {
            id: ao.id,
            // Filtering out additions that have caount 0
            additions: ao.additions
              .filter((a) => a.count > 0)
              .map((a) => ({
                id: a.id,
              })),
          }
        })

      return {
        id: p.id,
        // Only sending addition options if additions are chosen
        ...(additionOptions.length > 0
          ? { addition_options: additionOptions }
          : {}),
      }
    }),
  })

  return useQuery({
    queryKey: [cacheKeys.recommendedProducts, shopId, getPayload()],
    queryFn: async () => {
      const recommendedProducts =
        await api.recommendedProducts.getUpsellProducts(getPayload())
      return recommendedProducts
    },
    refetchOnWindowFocus: false,
    enabled: isOpen && !!shopId,
  })
}

export const useSignupInfo = () => {
  const anonymousAPI = useAnonymousAPI()

  return useQuery({
    queryKey: [cacheKeys.signup_info],
    queryFn: async () => await anonymousAPI.auth.signupInfo(),
    refetchOnWindowFocus: false,
  })
}

export const useGetOrder = (id?: string) => {
  const api = useAPI()
  const { language } = useI18next()

  return useQuery({
    queryKey: [cacheKeys.order, id, language],
    queryFn: async () => await api.checkout.retrieveOrderById(id, language),
    refetchOnWindowFocus: true,
    enabled: !!id,
  })
}

export const useOrderByDisplayId = (displayId: string) => {
  const api = useAnonymousAPI()

  return useQuery({
    queryKey: [cacheKeys.orderByDisplayId, displayId],
    queryFn: async () => await api.checkout.retrieveOrderByDisplayId(displayId),
    refetchOnWindowFocus: true,
    enabled: !!displayId,
  })
}

export const useInitCheckout = () => {
  const api = useAPI()

  return useMutation({
    mutationFn: api.checkout.init,
  })
}

export const useUpdateOrder = (id?: string) => {
  const api = useAPI()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: api.checkout.updateOrder,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [cacheKeys.order, id] })
    },
  })
}

export const useCancelPayment = (id?: string) => {
  const api = useAPI()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: api.payments.cancel,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [cacheKeys.order, id] })
    },
  })
}

export const useRemoveVoucherOrMembercardFromOrder = (orderId?: string) => {
  const api = useAPI()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: api.checkout.removeVoucherOrMembercard,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [cacheKeys.order, orderId] })
    },
  })
}

export const useRemoveGiftCardFromOrder = (orderId?: string) => {
  const api = useAPI()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: api.checkout.removeGiftCard,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [cacheKeys.order, orderId] })
    },
  })
}

export const useApplyVoucherForOrder = (orderId?: string) => {
  const api = useAPI()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: api.checkout.applyVoucher,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [cacheKeys.order, orderId] })
    },
  })
}

export const useApplyBenefitOnOrder = (orderId?: string) => {
  const api = useAPI()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: api.checkout.applyBenefitOnOrder,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [cacheKeys.order, orderId] })
    },
  })
}

export const useCreditCards = () => {
  const api = useAPI()

  return useQuery({
    queryKey: [cacheKeys.creditCards],
    queryFn: api.payments.getCreditCards,
  })
}

export const useDeleteCreditCard = (orderId?: string) => {
  const api = useAPI()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: api.payments.deleteCreditCard,
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: [cacheKeys.paymentMethods, orderId],
      })

      await queryClient.refetchQueries({
        queryKey: [cacheKeys.creditCards],
      })
    },
  })
}

export const useAddCreditCard = (orderId?: string) => {
  const api = useAPI()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: api.payments.addCreditCard,
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: [cacheKeys.paymentMethods, orderId],
      })
      await queryClient.refetchQueries({
        queryKey: [cacheKeys.creditCards],
      })
    },
  })
}

export const useValidateToken = () => {
  const api = useAnonymousAPI()

  return useMutation({
    mutationFn: api.passwordRecovery.validateToken,
  })
}

export const useChangePassword = () => {
  const api = useAnonymousAPI()

  return useMutation({
    mutationFn: api.passwordRecovery.resetPassword,
  })
}

export const usePaymentIntent = ({
  success_url,
  cancel_url,
  supported_providers,
}: {
  success_url: string
  cancel_url: string
  supported_providers: ProviderName[]
}) => {
  const api = useAPI()

  return useQuery({
    queryKey: [cacheKeys.paymentIntent, supported_providers],
    queryFn: async () => {
      return api.payments.createIntent({
        success_url,
        cancel_url,
        supported_providers,
      })
    },
    refetchOnWindowFocus: false,
    retry: false,
    // Disabled cache because we want to always get the latest provider
    gcTime: 0,
    staleTime: 0,
  })
}
